import React, { useState, useEffect } from 'react';
import { dataUrlToBlobUrl, viewAttachmentInNewTab } from '../utils/attachmentStorage';
import { formatDateIN } from '../utils/dateFormat';

const formatFileSize = (size) => {
  if (!size && size !== 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Reusable Attachment Details modal matching the Correspondence attachment viewer.
 * Shows metadata, document preview with zoom, and View/Download actions.
 * @param {Object} file - Attachment with name, size, type, data_url, uploaded_at
 * @param {Function} onClose - Called when modal is closed
 * @param {Function} onDownload - (file) => void
 * @param {Function} [onOpenInNewTab] - (file) => void, defaults to viewAttachmentInNewTab
 * @param {Array<{label:string,value:string}>} [extraMetadata] - Additional metadata rows e.g. [{label:'Project',value:'...'},{label:'Entry Ref',value:'...'}]
 */
const AttachmentDetailsModal = ({
  file,
  onClose,
  onDownload,
  onOpenInNewTab,
  extraMetadata = [],
}) => {
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!file?.data_url) return;
    const blob = dataUrlToBlobUrl(file.data_url);
    setPreviewBlobUrl(blob || null);
    return () => {
      if (blob) try { URL.revokeObjectURL(blob); } catch (_) {}
    };
  }, [file?.data_url]);

  const handleClose = () => {
    if (previewBlobUrl) { try { URL.revokeObjectURL(previewBlobUrl); } catch (_) {} setPreviewBlobUrl(null); }
    onClose?.();
  };

  const handleZoomIn = () => setZoom((z) => Math.min(2.5, z + 0.25));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25));
  const handleFitWidth = () => setZoom(1);

  const hasPreview = !!(file?.data_url || previewBlobUrl);
  const isImage = /^data:image\//i.test(file?.data_url || '');
  const isPdf = /pdf/i.test(file?.type || '');

  const openInNewTab = () => {
    if (typeof onOpenInNewTab === 'function') {
      onOpenInNewTab(file);
    } else {
      viewAttachmentInNewTab(file, () => onDownload?.(file));
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <h2>Attachment Details</h2>
          <button className="btn-close" onClick={handleClose}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><p style={{ fontSize: 12, color: '#6b7280' }}>File Name</p><p style={{ fontSize: 14 }}>{file?.name || '-'}</p></div>
            <div><p style={{ fontSize: 12, color: '#6b7280' }}>Type</p><p style={{ fontSize: 14 }}>{file?.type || 'application/octet-stream'}</p></div>
            <div><p style={{ fontSize: 12, color: '#6b7280' }}>Size</p><p style={{ fontSize: 14 }}>{formatFileSize(file?.size)}</p></div>
            <div><p style={{ fontSize: 12, color: '#6b7280' }}>Uploaded At</p><p style={{ fontSize: 14 }}>{formatDateIN(file?.uploaded_at)}</p></div>
            {extraMetadata.map((item, idx) => (
              <div key={idx}><p style={{ fontSize: 12, color: '#6b7280' }}>{item.label}</p><p style={{ fontSize: 14 }}>{item.value || '-'}</p></div>
            ))}
          </div>

          {hasPreview && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginTop: 16,
                padding: '8px 12px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" className="btn-secondary btn-sm" onClick={handleFitWidth} title="Fit width">⊡</button>
                  <button type="button" className="btn-secondary btn-sm" onClick={handleZoomOut} title="Zoom out">−</button>
                  <span style={{ fontSize: 13, minWidth: 60, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                  <button type="button" className="btn-secondary btn-sm" onClick={handleZoomIn} title="Zoom in">+</button>
                </div>
                {(isPdf || isImage) && <span style={{ fontSize: 12, color: '#6b7280' }}>Document preview</span>}
              </div>
              <div
                style={{
                  marginTop: 8,
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  overflow: 'auto',
                  maxHeight: 520,
                  minHeight: 400,
                  background: '#f9fafb',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: 16,
                }}
              >
                <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
                  {isImage ? (
                    <img
                      src={previewBlobUrl || file.data_url}
                      alt={file.name}
                      style={{ maxWidth: '100%', display: 'block' }}
                    />
                  ) : isPdf && previewBlobUrl ? (
                    <iframe
                      src={previewBlobUrl}
                      title={file.name}
                      style={{ width: 760, height: 580, border: 'none' }}
                    />
                  ) : (
                    <p style={{ padding: 24, fontSize: 13, color: '#6b7280' }}>
                      Preview not available. Use &quot;Open in New Tab&quot; or Download.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
          {!hasPreview && (
            <p style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
              This record has metadata only. Download will generate an attachment record file.
            </p>
          )}

          <div className="modal-actions" style={{ marginTop: 16 }}>
            <button className="btn-secondary" onClick={handleClose}>Close</button>
            <button className="btn-secondary" onClick={openInNewTab}>Open in New Tab</button>
            <button className="btn-primary" onClick={() => onDownload?.(file)}>Download</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentDetailsModal;
